import { FOG_FRAGMENT_GLSL, FOG_UNIFORMS, FOG_VERTEX_GLSL, type Fog } from "../camera";
import type { RgbaImage } from "../image";
import { WaterBob, waterFrame, type WaterMap } from "../watermap";
import {
  EMPTY_WINDOW,
  visibleCellWindow,
  windowContains,
  type CellWindow,
  type ViewProjection,
  type WorldBounds,
} from "../window";
import { createBuffer, createVertexArray, describeAttributes } from "./buffer";
import { GlError, checkGlError } from "./context";
import { createProgram, uniformLocations, type Uniforms } from "./shader";

/** x, y, z in millimetres and the corner's alpha, per vertex. */
export const WATER_VERTEX_FLOATS = 4;

/** `CELLSCALE`, 200 units (`PRTerrainLib/Terrain.h:54`), in millimetres. */
const CELL_MM = 2_000;
const MM_PER_UNIT = 10;
/** `m_fWaterTexCoordBase = 1 / (CELLSCALE * 4)` units (`MapOutdoorLoad.cpp:418`):
 *  one texture repeat per 800 units. */
const TEXTURE_REPEAT_MM = 800 * MM_PER_UNIT;
/** Cells of slack around the viewport before the vertices are rebuilt. */
const PAD_CELLS = 8;

/** Where the water texture is sampled at a world point. `RenderWater` feeds the
 *  camera-space position through the inverse view, which is the world position,
 *  and scales it by the base with v negated (`MapOutdoorWater.cpp:46-48`); the
 *  original's world y is the negative of this tree's, so the two flips cancel. */
export function waterTexCoord(xMm: number, yMm: number): [number, number] {
  return [xMm / TEXTURE_REPEAT_MM, yMm / TEXTURE_REPEAT_MM];
}

/** The water quads of every wet cell in a window, six vertices each in the
 *  order `_CalculateTerrainPatch` lays them -- (x, y), (x, y+1), (x+1, y),
 *  (x+1, y), (x, y+1), (x+1, y+1), each with its corner's alpha
 *  (`AreaTerrain.cpp:1056-1093`). Returns the vertex count; stops at the end of
 *  `out`. */
export function buildWaterVertices(water: WaterMap, window: CellWindow, out: Float32Array): number {
  let at = 0;
  for (let cellY = window.y0; cellY < window.y1; cellY += 1) {
    for (let cellX = window.x0; cellX < window.x1; cellX += 1) {
      const cell = water.cell(cellX, cellY);
      if (cell === null) continue;
      const zMm = water.surfaceZMm(cellX, cellY);
      if (zMm === null) continue;
      if (at + 6 * WATER_VERTEX_FLOATS > out.length) return at / WATER_VERTEX_FLOATS;
      const x0 = cellX * CELL_MM;
      const y0 = cellY * CELL_MM;
      const x1 = x0 + CELL_MM;
      const y1 = y0 + CELL_MM;
      const [a0, a1, a2, a3] = cell.alphas;
      const corners: readonly (readonly [number, number, number])[] = [
        [x0, y0, a0], [x0, y1, a1], [x1, y0, a2],
        [x1, y0, a2], [x0, y1, a1], [x1, y1, a3],
      ];
      for (const [x, y, alpha] of corners) {
        out[at++] = x;
        out[at++] = y;
        out[at++] = zMm;
        out[at++] = alpha / 255;
      }
    }
  }
  return at / WATER_VERTEX_FLOATS;
}

const VERTEX_SOURCE = `#version 300 es
precision highp float;

in vec3 a_position;
in float a_alpha;

uniform mat4 u_viewProj;
uniform float u_bobMm;
uniform float u_repeatMm;

out vec2 v_uv;
out float v_alpha;
${FOG_VERTEX_GLSL}
void main() {
  vec3 world = a_position + vec3(0.0, 0.0, u_bobMm);
  v_uv = world.xy / u_repeatMm;
  v_alpha = a_alpha;
  v_fog = fogFactor(world.xy);
  gl_Position = u_viewProj * vec4(world, 1.0);
}
`;

const FRAGMENT_SOURCE = `#version 300 es
precision highp float;
precision highp sampler2DArray;

in vec2 v_uv;
in float v_alpha;

uniform sampler2DArray u_frames;
uniform float u_frame;

out vec4 fragColour;
${FOG_FRAGMENT_GLSL}
void main() {
  // D3DTOP_SELECTARG1 on the texture for colour, on the diffuse for alpha
  // (\`MapOutdoorWater.cpp:61-64\`).
  vec3 colour = texture(u_frames, vec3(v_uv, u_frame)).rgb;
  fragColour = vec4(applyFog(colour), v_alpha);
}
`;

const UNIFORMS = ["u_viewProj", "u_bobMm", "u_repeatMm", "u_frames", "u_frame", ...FOG_UNIFORMS] as const;

/** `CMapOutdoor::RenderWater` (`MapOutdoorWater.cpp:25-140`): every wet cell in
 *  view drawn at its body's height plus the surface's bob, textured by one of
 *  thirty frames mapped flat on the world, with depth writes off, blended by the
 *  corner alpha and fogged with the rest of the world. The original draws the
 *  patches past the fog distance untextured and unblended; the fog here ends
 *  before that distance, so what those patches show is the fog colour either way. */
export class GlWater {
  private readonly program: WebGLProgram;
  private readonly uniforms: Uniforms<(typeof UNIFORMS)[number]>;
  private readonly vao: WebGLVertexArrayObject;
  private readonly buffer: WebGLBuffer;
  private readonly frames: WebGLTexture;
  private readonly bob: WaterBob;
  private staging = new Float32Array(0);
  private built: CellWindow = EMPTY_WINDOW;
  private vertices = 0;

  constructor(
    private readonly gl: WebGL2RenderingContext,
    private readonly water: WaterMap,
    frames: readonly RgbaImage[],
    startMs: number,
  ) {
    if (frames.length === 0) throw new GlError("no water frames to draw with");
    this.program = createProgram(gl, VERTEX_SOURCE, FRAGMENT_SOURCE, "water");
    this.uniforms = uniformLocations(gl, this.program, UNIFORMS);
    this.bob = new WaterBob(startMs);

    this.buffer = createBuffer(gl, "water");
    this.vao = createVertexArray(gl, "water");
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    describeAttributes(gl, this.program, WATER_VERTEX_FLOATS * 4, [
      { name: "a_position", components: 3, type: gl.FLOAT, normalized: false, offset: 0, divisor: 0 },
      { name: "a_alpha", components: 1, type: gl.FLOAT, normalized: false, offset: 12, divisor: 0 },
    ]);
    gl.bindVertexArray(null);

    const texture = gl.createTexture();
    if (texture === null) throw new GlError("could not create the water frames");
    this.frames = texture;
    const { width, height } = frames[0];
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, texture);
    gl.texImage3D(gl.TEXTURE_2D_ARRAY, 0, gl.RGBA8, width, height, frames.length, 0,
      gl.RGBA, gl.UNSIGNED_BYTE, null);
    frames.forEach((frame, layer) => {
      gl.texSubImage3D(gl.TEXTURE_2D_ARRAY, 0, 0, 0, layer, width, height, 1,
        gl.RGBA, gl.UNSIGNED_BYTE, frame.pixels);
    });
    gl.generateMipmap(gl.TEXTURE_2D_ARRAY);
    // Linear with linear mips, wrapping (`MapOutdoorWater.cpp:55-59`).
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.REPEAT);
    checkGlError(gl, "water frames upload");
  }

  /** The level a water surface sits at under a point, or null: `GetWaterHeight`. */
  heightAt(xMm: number, yMm: number): number | null {
    return this.water.heightAt(xMm, yMm);
  }

  draw(view: ViewProjection, bounds: WorldBounds, fog: Fog, nowMs: number): void {
    const grid = {
      baseXMm: 0, baseYMm: 0, cellMm: CELL_MM,
      worldCellsX: this.water.cellsX, worldCellsY: this.water.cellsY,
    };
    if (!windowContains(this.built, visibleCellWindow(grid, bounds, 1))) {
      this.rebuild(visibleCellWindow(grid, bounds, PAD_CELLS));
    }
    const offsetMm = this.bob.offsetMm(nowMs);
    if (this.vertices === 0) return;

    const gl = this.gl;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.frames);
    gl.uniform1i(this.uniforms.u_frames, 0);
    gl.uniform1f(this.uniforms.u_frame, waterFrame(nowMs));
    gl.uniformMatrix4fv(this.uniforms.u_viewProj, false, view.viewProj);
    gl.uniform1f(this.uniforms.u_bobMm, offsetMm);
    gl.uniform1f(this.uniforms.u_repeatMm, TEXTURE_REPEAT_MM);
    gl.uniform2f(this.uniforms.u_fogCameraMm, fog.cameraXMm, fog.cameraYMm);
    gl.uniform2f(this.uniforms.u_fogRangeMm, fog.nearMm, fog.farMm);
    gl.uniform3f(this.uniforms.u_fogColour, fog.colour[0], fog.colour[1], fog.colour[2]);

    // Depth writes off, culling off, blended (`MapOutdoorWater.cpp:36-40`),
    // with the device's SRCALPHA/INVSRCALPHA pair (`StateManager.cpp:205-206`).
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(false);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.drawArrays(gl.TRIANGLES, 0, this.vertices);
    gl.disable(gl.BLEND);
    gl.depthMask(true);
    gl.bindVertexArray(null);
  }

  private rebuild(window: CellWindow): void {
    const cells = Math.max(0, window.x1 - window.x0) * Math.max(0, window.y1 - window.y0);
    const needed = cells * 6 * WATER_VERTEX_FLOATS;
    if (needed > this.staging.length) this.staging = new Float32Array(needed);
    this.vertices = buildWaterVertices(this.water, window, this.staging);
    this.built = window;
    if (this.vertices === 0) return;
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.staging.subarray(0, this.vertices * WATER_VERTEX_FLOATS), gl.DYNAMIC_DRAW);
  }
}
